import React, { useState } from "react";

import andUsLogo from "../../images/logolight.png";
import tpcLogo from "../../images/tpc-performance-centre-logo.png";
import footerBackground from "../../images/andus-footer-luminous-background.svg";
import arrowUpIcon from "../../images/andus-footer-arrow-up.svg";
import emailIcon from "../../images/andus-footer-email.svg";
import facebookIcon from "../../images/andus-footer-facebook.svg";
import instagramIcon from "../../images/andus-footer-instagram.svg";
import linkedinIcon from "../../images/andus-footer-linkedin.svg";
import locationIcon from "../../images/andus-footer-location.svg";
import phoneIcon from "../../images/andus-footer-phone.svg";
import youtubeIcon from "../../images/andus-footer-youtube.svg";

import "./AndUsFooter.css";

const TPC_WEBSITE = "https://techperformancecentre.com/";

const TPC_LOCATION =
  "https://www.google.com/maps/search/?api=1&query=Floor+2+Landmark+Building+13+Umgazi+Road+Menlo+Park+Pretoria+0081";

const DEFAULT_SOCIAL_LINKS = {
  linkedin: "https://za.linkedin.com/company/tpc-performance-centre",
  instagram: null,
  facebook: null,
  youtube: null,
};

const CONTACT_CHANNELS = [
  {
    id: "reception",
    icon: phoneIcon,
    eyebrow: "Reception",
    value: "012 051 3483",
    description: "TPC Head Office reception",
    href: "tel:+27120513483",
    tone: "blue",
  },
  {
    id: "support",
    icon: phoneIcon,
    eyebrow: "TPC Support Line",
    value: "+27 82 732 3216",
    description: "Shared support channel",
    href: "tel:+27827323216",
    tone: "blue",
  },
  {
    id: "email",
    icon: emailIcon,
    eyebrow: "Shared Enquiries",
    value: "info@tpc.training",
    description: "Group-managed public inbox",
    href: "mailto:info@tpc.training",
    tone: "blue",
  },
  {
    id: "location",
    icon: locationIcon,
    eyebrow: "TPC Head Office · Pretoria",
    value: "Floor 2, Landmark Building",
    description: (
      <>
        13 Umgazi Rd, Menlo Park
        <br />
        Pretoria, 0081
      </>
    ),
    href: TPC_LOCATION,
    tone: "pink",
    external: true,
  },
];

const FOOTER_NAVIGATION = [
  {
    id: "systems",
    title: "Systems",
    links: [
      { label: "Unity Learning", href: "/#unity-learning" },
      { label: "TalentLenz", href: "/#talentlenz" },
      { label: "FleetCore", href: "/#fleetcore" },
      { label: "Custom Systems", href: "/#custom-systems" },
    ],
  },
  {
    id: "company",
    title: "Company",
    links: [
      { label: "About AndUs", href: "/#top" },
      { label: "How We Work", href: "/#custom-systems" },
      { label: "Part of the TPC Group", href: "/#company" },
      { label: "Contact", href: "/#contact" },
    ],
  },
  {
    id: "legal",
    title: "Legal",
    links: [
      { label: "Privacy & POPIA", href: "/privacy" },
      { label: "Terms of Use", href: "/terms" },
      { label: "Cookie Notice", href: "/cookies" },
    ],
  },
];

const SOCIAL_CHANNELS = [
  {
    id: "linkedin",
    label: "LinkedIn",
    icon: linkedinIcon,
  },
  {
    id: "instagram",
    label: "Instagram",
    icon: instagramIcon,
  },
  {
    id: "facebook",
    label: "Facebook",
    icon: facebookIcon,
  },
  {
    id: "youtube",
    label: "YouTube",
    icon: youtubeIcon,
  },
];

function ContactCard({ channel }) {
  const {
    icon,
    eyebrow,
    value,
    description,
    href,
    tone,
    external,
  } = channel;

  return (
    <a
      className={`andus-footer__contact-card andus-footer__contact-card--${tone}`}
      href={href}
      target={external ? "_blank" : undefined}
      rel={external ? "noopener noreferrer" : undefined}
    >
      <span className="andus-footer__contact-icon">
        <img src={icon} alt="" aria-hidden="true" />
      </span>

      <span className="andus-footer__contact-content">
        <span className="andus-footer__contact-eyebrow">
          {eyebrow}
        </span>

        <strong>{value}</strong>
      </span>

      <span className="andus-footer__contact-description">
        {description}
      </span>
    </a>
  );
}

function SocialLink({ channel, href }) {
  const content = (
    <img
      src={channel.icon}
      alt=""
      aria-hidden="true"
    />
  );

  if (!href) {
    return (
      <span
        className="andus-footer__social-link is-unavailable"
        aria-label={`${channel.label} channel link not configured`}
        aria-disabled="true"
        title={`${channel.label} link not configured`}
      >
        {content}
      </span>
    );
  }

  return (
    <a
      className="andus-footer__social-link"
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={`Visit TPC on ${channel.label}`}
    >
      {content}
    </a>
  );
}

function NavigationGroup({
  group,
  expanded,
  onToggle,
}) {
  const panelId = `andus-footer-${group.id}-links`;

  return (
    <section
      className={`andus-footer__nav-group ${
        expanded ? "is-expanded" : ""
      }`}
    >
      <h3 className="andus-footer__nav-heading">
        {group.title}
      </h3>

      <button
        type="button"
        className="andus-footer__accordion-button"
        aria-expanded={expanded}
        aria-controls={panelId}
        onClick={onToggle}
      >
        <span>{group.title}</span>
        <span
          className="andus-footer__accordion-plus"
          aria-hidden="true"
        />
      </button>

      <ul
        id={panelId}
        className="andus-footer__nav-list"
      >
        {group.links.map((link) => (
          <li key={link.label}>
            <a href={link.href}>{link.label}</a>
          </li>
        ))}
      </ul>
    </section>
  );
}

export default function AndUsFooter({
  socialLinks = {},
}) {
  const [expandedGroups, setExpandedGroups] = useState({});
  const currentYear = new Date().getFullYear();

  const resolvedSocialLinks = {
    ...DEFAULT_SOCIAL_LINKS,
    ...socialLinks,
  };

  const toggleGroup = (groupId) => {
    setExpandedGroups((current) => ({
      ...current,
      [groupId]: !current[groupId],
    }));
  };

  const handleBackToTop = () => {
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    window.scrollTo({
      top: 0,
      left: 0,
      behavior: prefersReducedMotion ? "auto" : "smooth",
    });
  };

  return (
    <footer
      id="site-footer"
      className="andus-footer"
      aria-label="AndUs site footer"
    >
      <img
        className="andus-footer__background"
        src={footerBackground}
        alt=""
        aria-hidden="true"
      />

      <div className="andus-footer__container">
        <section className="andus-footer__identity">
          <div className="andus-footer__brand">
            <a
              className="andus-footer__logo-link"
              href="/#top"
              aria-label="AndUs home"
            >
              <img
                className="andus-footer__andus-logo"
                src={andUsLogo}
                alt="AndUs"
              />
            </a>

            <div className="andus-footer__brand-copy">
              <p className="andus-footer__blue-eyebrow">
                AndUs Intelligent Systems
              </p>

              <h2>
                Intelligent systems built
                <br />
                for real operations.
              </h2>

              <p>
                Custom intelligence and SaaS platforms for learning,
                talent, fleet management and operational
                productivity.
              </p>
            </div>
          </div>

          <div className="andus-footer__group">
            <div className="andus-footer__group-copy">
              <p className="andus-footer__gold-eyebrow">
                Part of the Performance Centre Group
              </p>

              <h2>
                One group. Shared infrastructure.
                <br />
                Applied capability.
              </h2>

              <p>
                AndUs is the TPC Group’s applied-intelligence
                technology subsidiary, based at its Pretoria head
                office.
              </p>
            </div>

            <div className="andus-footer__group-meta">
              <a
                className="andus-footer__tpc-logo-link"
                href={TPC_WEBSITE}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Visit the Tech Performance Centre website"
              >
                <img
                  className="andus-footer__tpc-logo"
                  src={tpcLogo}
                  alt="TPC Performance Centre"
                />
              </a>

              <a
                className="andus-footer__office-badge"
                href={TPC_LOCATION}
                target="_blank"
                rel="noopener noreferrer"
              >
                <span aria-hidden="true" />
                TPC Head Office · Pretoria
              </a>
            </div>
          </div>
        </section>

        <section
          className="andus-footer__contacts"
          aria-labelledby="andus-footer-contacts-title"
        >
          <div className="andus-footer__section-intro">
            <h2 id="andus-footer-contacts-title">
              Shared Contact Channels
            </h2>

            <p>
              AndUs enquiries are received through TPC’s established
              reception and support infrastructure.
            </p>
          </div>

          <div className="andus-footer__contact-grid">
            {CONTACT_CHANNELS.map((channel) => (
              <ContactCard
                key={channel.id}
                channel={channel}
              />
            ))}
          </div>
        </section>

        <section className="andus-footer__navigation">
          <div className="andus-footer__summary">
            <h2>AndUs Intelligent Systems</h2>

            <p className="andus-footer__summary-copy">
              Intelligence applied to the work that moves your
              organisation forward.
            </p>

            <p className="andus-footer__social-heading">
              Shared TPC Channels
            </p>

            <div className="andus-footer__social-row">
              {SOCIAL_CHANNELS.map((channel) => (
                <SocialLink
                  key={channel.id}
                  channel={channel}
                  href={resolvedSocialLinks[channel.id]}
                />
              ))}
            </div>
          </div>

          <nav
            className="andus-footer__nav"
            aria-label="Footer navigation"
          >
            {FOOTER_NAVIGATION.map((group) => (
              <NavigationGroup
                key={group.id}
                group={group}
                expanded={Boolean(expandedGroups[group.id])}
                onToggle={() => toggleGroup(group.id)}
              />
            ))}
          </nav>

          <button
            type="button"
            className="andus-footer__back-to-top"
            onClick={handleBackToTop}
          >
            <span>Back to top</span>
            <img src={arrowUpIcon} alt="" aria-hidden="true" />
          </button>
        </section>

        <div className="andus-footer__bottom">
          <p>
            © {currentYear} AndUs Intelligent Systems. All rights
            reserved.
          </p>

          <p>
            A technology subsidiary of The Performance Centre Group
          </p>

          <p className="andus-footer__bottom-location">
            <span aria-hidden="true" />
            Based at TPC Head Office · Pretoria
          </p>
        </div>
      </div>
    </footer>
  );
}